<template>
  <section
    v-if="status?.onboarding_call_due"
    class="onboarding-call-card d-flex flex-column flex-md-row align-items-md-center gap-3"
    data-testid="onboarding-call-card"
  >
    <div class="host-avatars flex-shrink-0" aria-hidden="true">
      <img
        v-for="photo in HOST_PHOTOS"
        :key="photo"
        :src="photo"
        alt=""
        class="host-avatar"
        loading="lazy"
      />
    </div>

    <div class="flex-grow-1">
      <h2 class="call-title font-serif mb-1">
        {{
          isRequested
            ? $t("billing.onboarding_call.requested_title")
            : $t("billing.onboarding_call.title")
        }}
      </h2>
      <p class="text-body-secondary mb-0">
        {{
          isRequested
            ? $t("billing.onboarding_call.requested_body")
            : $t("billing.onboarding_call.body")
        }}
      </p>
    </div>

    <div class="d-flex flex-column flex-sm-row gap-2 flex-shrink-0">
      <button
        type="button"
        class="btn"
        :class="isRequested ? 'btn-outline-primary' : 'btn-primary'"
        :disabled="isSaving"
        data-testid="onboarding-call-book"
        @click="handleBook"
      >
        <FontAwesomeIcon icon="calendar-check" class="me-2" />
        {{
          isRequested
            ? $t("billing.onboarding_call.book_again")
            : $t("billing.onboarding_call.book")
        }}
      </button>
      <button
        v-if="isRequested"
        type="button"
        class="btn btn-primary"
        :disabled="isSaving"
        data-testid="onboarding-call-booked"
        @click="record('booked')"
      >
        {{ $t("billing.onboarding_call.booked") }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * The first thing a paying corporation does: book an onboarding call with its account manager. Shown at the
 * top of the corporate and trader dashboards while the subscription is paid and the call is not booked. Opening
 * the booking page records the request; the customer then confirms the booking, which hides the card. Both
 * steps are kept on the company's billing row for the account managers. Never shown on a white-label portal.
 */
import { billingApi } from "@/billing/billingApi";
import type {
  BillingStatus,
  DemoOrganisation,
  OnboardingCallState,
} from "@/billing/billingTypes";
import { isWhiteLabel } from "@/branding";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Company } from "@/types";
import { computed, onMounted, ref } from "vue";

const HOST_PHOTOS = [
  "https://www.soldera.org/images/demo/host-roger.jpg",
  "https://www.soldera.org/images/demo/host-ronny.jpg",
  "https://www.soldera.org/images/demo/host-arturas.jpg",
  "https://www.soldera.org/images/demo/host-stenver.jpg",
];

const props = defineProps<{
  company: Company;
  /** Preselects the role on the booking page: the dashboard this card is on. */
  organisation: DemoOrganisation;
}>();

const status = ref<BillingStatus | null>(null);
const isSaving = ref(false);

const { globalErrorHandler } = useGlobalHandlers();

const isRequested = computed(
  () => status.value?.onboarding_call_requested_at != null
);

const bookingUrl = computed(() => {
  if (!status.value) return "";
  const url = new URL(status.value.onboarding_call_url);
  url.searchParams.set("organisation", props.organisation);
  return url.toString();
});

onMounted(async () => {
  if (isWhiteLabel()) return;
  try {
    status.value = (await billingApi.getStatus(props.company.id!)).data;
  } catch (error) {
    globalErrorHandler(error);
  }
});

function handleBook() {
  // Opened before the request so the browser still treats it as a direct result of the click.
  window.open(bookingUrl.value, "_blank", "noopener");
  if (!isRequested.value) void record("requested");
}

async function record(state: OnboardingCallState) {
  isSaving.value = true;
  try {
    status.value = (
      await billingApi.recordOnboardingCall(props.company.id!, state)
    ).data;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped lang="scss">
.onboarding-call-card {
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  border: 1px solid #d9bc78;
  background: #faecca;
}

.call-title {
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a2820;
}

.host-avatars {
  display: flex;
}

.host-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #faecca;
  background: #d9bc78;

  & + & {
    margin-left: -16px;
  }
}
</style>
